import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type {
  CandidateInput,
  InterviewInput,
  JobOpeningInput,
  OfferInput,
} from './types';

export const recruitmentKeys = {
  openings: ['recruitment', 'openings'] as const,
  candidates: ['recruitment', 'candidates'] as const,
  interviews: ['recruitment', 'interviews'] as const,
  offers: ['recruitment', 'offers'] as const,
};

// ---- Openings ----
export function useOpenings(params: api.OpeningListParams = {}) {
  return useQuery({
    queryKey: [...recruitmentKeys.openings, params],
    queryFn: () => api.listOpenings(params),
    placeholderData: keepPreviousData,
  });
}

export function useOpening(id: number | string) {
  return useQuery({
    queryKey: [...recruitmentKeys.openings, 'detail', String(id)],
    queryFn: () => api.getOpening(id),
    enabled: id !== '' && id != null,
  });
}

export function useCreateOpening() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: JobOpeningInput) => api.createOpening(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.openings });
      toast.success('Opening created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateOpening() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | string; input: Partial<JobOpeningInput> }) =>
      api.updateOpening(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.openings });
      toast.success('Opening updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteOpening() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => api.deleteOpening(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.openings });
      toast.success('Opening deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Candidates ----
export function useCandidates(params: api.CandidateListParams = {}) {
  return useQuery({
    queryKey: [...recruitmentKeys.candidates, params],
    queryFn: () => api.listCandidates(params),
    placeholderData: keepPreviousData,
  });
}

export function useCandidate(id: number | string) {
  return useQuery({
    queryKey: [...recruitmentKeys.candidates, 'detail', String(id)],
    queryFn: () => api.getCandidate(id),
    enabled: id !== '' && id != null,
  });
}

export function useCreateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CandidateInput) => api.createCandidate(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.candidates });
      toast.success('Candidate added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | string; input: Partial<CandidateInput> }) =>
      api.updateCandidate(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: recruitmentKeys.candidates }),
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => api.deleteCandidate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.candidates });
      toast.success('Candidate removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Interviews ----
export function useInterviews(params: api.InterviewListParams = {}) {
  return useQuery({
    queryKey: [...recruitmentKeys.interviews, params],
    queryFn: () => api.listInterviews(params),
    placeholderData: keepPreviousData,
  });
}

export function useInterview(id: number | string) {
  return useQuery({
    queryKey: [...recruitmentKeys.interviews, 'detail', String(id)],
    queryFn: () => api.getInterview(id),
    enabled: id !== '' && id != null,
  });
}

export function useCreateInterview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InterviewInput) => api.createInterview(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.interviews });
      toast.success('Interview scheduled');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateInterview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | string; input: Partial<InterviewInput> }) =>
      api.updateInterview(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.interviews });
      toast.success('Interview updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteInterview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => api.deleteInterview(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.interviews });
      toast.success('Interview deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Offers ----
export function useOffers(params: api.OfferListParams = {}) {
  return useQuery({
    queryKey: [...recruitmentKeys.offers, params],
    queryFn: () => api.listOffers(params),
    placeholderData: keepPreviousData,
  });
}

export function useOffer(id: number | string) {
  return useQuery({
    queryKey: [...recruitmentKeys.offers, 'detail', String(id)],
    queryFn: () => api.getOffer(id),
    enabled: id !== '' && id != null,
  });
}

export function useCreateOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OfferInput) => api.createOffer(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.offers });
      toast.success('Offer created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | string; input: Partial<OfferInput> }) =>
      api.updateOffer(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.offers });
      toast.success('Offer updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => api.deleteOffer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recruitmentKeys.offers });
      toast.success('Offer deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
